import { type ReactNode } from 'react';
import { MoreVertical, type LucideIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Tooltip } from '../ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';

/**
 * Row actions (BOT-392).
 *
 * The review's complaint was that actions looked like prose links, read as
 * un-clickable, and grew the column every time a new one was added. So: the
 * two actions an operator uses constantly stay visible as icon buttons, and
 * everything else moves behind an overflow menu — which keeps the column at a
 * fixed width no matter how many actions exist.
 */
export function RowActions({ children }: { children: ReactNode }) {
  return <div className="flex items-center justify-end gap-1">{children}</div>;
}

type Tone = 'default' | 'success';

export function RowActionButton({
  label,
  icon: Icon,
  onClick,
  disabled,
  tone = 'default',
}: {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  disabled?: boolean;
  tone?: Tone;
}) {
  return (
    <Tooltip label={label}>
      <Button
        variant="action"
        size="icon"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        className={cn(
          tone === 'success'
            ? 'text-green-600 hover:border-green-200 hover:bg-green-50'
            : 'text-zinc-600 hover:bg-accent',
        )}
      >
        <Icon className="h-3.5 w-3.5" strokeWidth={tone === 'success' ? 2.5 : 2} />
      </Button>
    </Tooltip>
  );
}

/** Same affordance as `RowActionButton` for an action that is a navigation.
 *  Always opens in a new tab — the review asked for this on "Details", so the
 *  list keeps its scroll position and filters while the item is read. */
export function RowActionLink({
  label,
  icon: Icon,
  href,
}: {
  label: string;
  icon: LucideIcon;
  href: string;
}) {
  return (
    <Tooltip label={label}>
      <Button variant="action" size="icon" asChild className="text-zinc-600 hover:bg-accent">
        <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
          <Icon className="h-3.5 w-3.5" />
        </a>
      </Button>
    </Tooltip>
  );
}

export interface RowMenuItem {
  label: string;
  icon: LucideIcon;
  onSelect?: () => void;
  href?: string;
  destructive?: boolean;
  /** Draws a divider above this item. */
  separatorBefore?: boolean;
  disabled?: boolean;
}

export function RowActionMenu({ items }: { items: RowMenuItem[] }) {
  if (items.length === 0) return null;
  return (
    <DropdownMenu>
      <Tooltip label="More actions">
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label="More actions"
            className="border border-transparent hover:border-border"
          >
            <MoreVertical className="h-[15px] w-[15px]" />
          </Button>
        </DropdownMenuTrigger>
      </Tooltip>
      <DropdownMenuContent className="w-[190px]">
        {items.map(item => {
          const Icon = item.icon;
          const content = (
            <>
              <Icon className="h-3.5 w-3.5" />
              {item.label}
            </>
          );
          return (
            <div key={item.label}>
              {item.separatorBefore && <DropdownMenuSeparator />}
              <DropdownMenuItem
                destructive={item.destructive}
                disabled={item.disabled}
                onSelect={item.onSelect}
                asChild={!!item.href}
              >
                {item.href ? (
                  <a href={item.href} target="_blank" rel="noopener noreferrer">
                    {content}
                  </a>
                ) : (
                  content
                )}
              </DropdownMenuItem>
            </div>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Terminal state for a row that has just been actioned, with an undo affordance. */
export function RowActedLabel({
  label,
  tone,
  onUndo,
}: {
  label: string;
  tone: 'success' | 'muted';
  onUndo?: () => void;
}) {
  return (
    <div className="flex items-center justify-end gap-1.5 text-xs font-medium">
      <span className={tone === 'success' ? 'text-green-700' : 'text-muted-foreground'}>{label}</span>
      {onUndo && (
        <button
          type="button"
          onClick={onUndo}
          className="text-[11.5px] text-muted-foreground underline hover:text-foreground"
        >
          Undo
        </button>
      )}
    </div>
  );
}
